import { useAuth } from "@clerk/clerk-react";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Logo } from "@the-drop/ui";
import { resolveClerkPublishableKey } from "../clerk-env";
import { AuthHeader } from "../auth-header";
import { useCartStore } from "../store/cart-store";

function IconMarketplace({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

function IconOrders({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 13V7a2 2 0 00-2-2H6a2 2 0 00-2 2v6m16 0v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );
}

function IconUser({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

function navLinkClass(active: boolean): string {
  const base = "text-sm transition-colors";
  return active
    ? `${base} font-semibold text-[var(--green)]`
    : `${base} font-medium text-[var(--text-secondary)] hover:text-[var(--green)]`;
}

function tabClass(active: boolean): string {
  const base = "flex flex-col items-center justify-center gap-1";
  return active ? `${base} text-[var(--green)]` : `${base} text-[var(--text-muted)]`;
}

export function CustomerShell() {
  if (!resolveClerkPublishableKey()) {
    return <CustomerShellLayout showAppNav={false} />;
  }
  return <CustomerShellWithClerk />;
}

function CustomerShellWithClerk() {
  const { isLoaded, isSignedIn } = useAuth();
  /** Signed-out: no app nav (no center nav, no cart pill, no bottom tabs). */
  return <CustomerShellLayout showAppNav={isLoaded && isSignedIn} />;
}

function CustomerShellLayout({ showAppNav }: { showAppNav: boolean }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isHome = pathname === "/";
  const isOrders = pathname === "/orders" || pathname.startsWith("/account/orders");
  const isAccountNav = pathname.startsWith("/account") && !pathname.startsWith("/account/orders");

  return (
    <div className="flex min-h-screen flex-col bg-[var(--page-bg)]">
      <header className="sticky top-0 z-30 border-b border-[var(--card-border)] bg-white/95 px-4 backdrop-blur lg:px-7">
        <div className="mx-auto flex h-16 w-full max-w-[1200px] items-center justify-between gap-5">
          <Link to="/" className="inline-block shrink-0" aria-label="Drop home">
            <Logo size="lg" tone="dark" />
          </Link>

          {showAppNav ? (
            <nav className="hidden items-center gap-6 lg:flex">
              <Link to="/" className={navLinkClass(isHome)}>
                Marketplace
              </Link>
              <Link to="/orders" className={navLinkClass(isOrders)}>
                Orders
              </Link>
              <Link to="/account" className={navLinkClass(isAccountNav)}>
                Account
              </Link>
            </nav>
          ) : null}

          <div className="flex shrink-0 items-center gap-3 sm:gap-4">
            {showAppNav ? <CartPill /> : null}
            <AuthHeader />
          </div>
        </div>
      </header>

      <main className={`flex-1 px-4 lg:px-7 ${showAppNav ? "pb-24 lg:pb-7" : "pb-7"}`}>
        <Outlet />
      </main>

      {showAppNav ? (
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 grid h-16 grid-cols-3 border-t border-[var(--card-border)] bg-white pb-[env(safe-area-inset-bottom)] lg:hidden"
          aria-label="Primary"
        >
          <Link to="/" className={tabClass(isHome)}>
            <IconMarketplace className="h-5 w-5" />
            <span className="text-2xs font-medium">Marketplace</span>
          </Link>
          <Link to="/orders" className={tabClass(isOrders)}>
            <IconOrders className="h-5 w-5" />
            <span className="text-2xs font-medium">Orders</span>
          </Link>
          <Link to="/account" className={tabClass(isAccountNav)}>
            <IconUser className="h-5 w-5" />
            <span className="text-2xs font-medium">Account</span>
          </Link>
        </nav>
      ) : null}
    </div>
  );
}

/**
 * Cart count pill in the header.
 *
 * Per locked decision #2 the customer can have parallel per-dispensary carts.
 * The pill shows the grand total across all carts and routes the click to a
 * sensible single destination:
 *   1. If the user is on a `/$slug/...` route and that dispensary has a non-
 *      empty cart, jump to that dispensary's cart.
 *   2. Else, fall back to the first dispensary slug whose cart has items.
 *
 * Multi-cart switching UX (a list/dropdown) is deferred — PR 1.4 owns the
 * cart screen and is the right place to surface "you also have a cart at X."
 * Hidden when every cart is empty.
 */
function CartPill() {
  const carts = useCartStore((s) => s.carts);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  let totalItems = 0;
  for (const lines of Object.values(carts)) {
    for (const line of lines) {
      const q = Math.floor(Number(line.quantity));
      if (Number.isFinite(q) && q > 0) totalItems += q;
    }
  }

  const dispMatch = pathname.match(/^\/([^/]+)/);
  const candidate = dispMatch?.[1];
  const currentSlug =
    candidate && carts[candidate] && carts[candidate].length > 0 ? candidate : null;
  const fallbackSlug = currentSlug
    ? null
    : Object.entries(carts).find(([, lines]) => lines.length > 0)?.[0] ?? null;
  const targetSlug = currentSlug ?? fallbackSlug;

  // Always-visible: when no items, render a non-link "Cart" placeholder so
  // customers see the surface and learn the affordance. When a cart has
  // items, link to the most-relevant per-dispensary cart screen.
  if (totalItems === 0 || !targetSlug) {
    return (
      <span
        className="text-sm font-medium text-[var(--text-muted)]"
        aria-label="Cart, empty"
      >
        Cart
      </span>
    );
  }

  return (
    <Link
      to="/$dispensarySlug/cart"
      params={{ dispensarySlug: targetSlug }}
      className="text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
      aria-label={`Cart, ${totalItems} item${totalItems === 1 ? "" : "s"}`}
    >
      Cart ({totalItems})
    </Link>
  );
}
